import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { useSelector } from 'react-redux';

const ServerSideTable = forwardRef(({ columns, apiUrl, searchTerm = '', refreshTable = '', queryKey = {} }, ref) => {
    const { userToken } = useSelector((state) => state.auth);

    const defaultColDef = useMemo(
        () => ({
            flex: 1,
            minWidth: 150,
            sortable: true,
            filter: true
        }),
        []
    );

    const paginationPageSizeSelector = useMemo(() => [10, 20, 50, 100], []);

    const [gridApi, setGridApi] = useState(null);
    const searchTermRef = useRef(searchTerm);
    const gridParamsRef = useRef(null);

    const getDatasource = useCallback(
        () => ({
            getRows: async (params) => {
                const { startRow, endRow, sortModel, filterModel } = params;

                const pageSize = endRow - startRow;
                const page = startRow / pageSize + 1;

                const query = new URLSearchParams({
                    page: page.toString(),
                    pageSize: pageSize.toString(),
                    sortModel: JSON.stringify(sortModel),
                    filterModel: JSON.stringify(filterModel),
                    search: searchTermRef.current,
                    ...queryKey
                });

                try {
                    const response = await fetch(`${apiUrl}${query.toString()}`, {
                        headers: {
                            Authorization: `Bearer ${userToken}`,
                            'Content-Type': 'application/json'
                        }
                    });

                    const data = await response.json();
                    const rowsThisPage = data?.data || [];
                    const lastRow = data?.pagination?.totalRecords || 0;

                    params.successCallback(rowsThisPage, lastRow);
                } catch (error) {
                    params.failCallback();
                }
            }
        }),
        [apiUrl, queryKey, userToken]
    );

    const onGridReady = useCallback(
        (params) => {
            if (params?.api) {
                setGridApi(params.api);
                gridParamsRef.current = params;

                const datasource = getDatasource();
                params.api.setDatasource(datasource);
            }
        },
        [getDatasource]
    );

    useEffect(() => {
        searchTermRef.current = searchTerm;

        if (gridApi && gridParamsRef.current) {
            const datasource = getDatasource();
            gridApi.setDatasource(datasource);
            gridApi.refreshInfiniteCache();
        }
    }, [searchTerm, refreshTable, apiUrl, getDatasource]);

    useImperativeHandle(
        ref,
        () => ({
            refresh: () => {
                if (gridApi && gridParamsRef.current) {
                    const datasource = getDatasource();
                    gridApi.setDatasource(datasource);
                    gridApi.refreshInfiniteCache();
                }
            }
        }),
        [gridApi, getDatasource]
    );

    return (
        <main className="parker-table-container mt-4">
            <div className="ag-theme-quartz" style={{ height: 550, width: '100%' }}>
                <AgGridReact
                    columnDefs={columns}
                    defaultColDef={defaultColDef}
                    paginationPageSizeSelector={paginationPageSizeSelector}
                    rowModelType="infinite"
                    pagination={true}
                    paginationPageSize={10}
                    cacheBlockSize={10}
                    onGridReady={onGridReady}
                    overlayLoadingTemplate='<span class="ag-overlay-loading-center">Loading...</span>'
                    overlayNoRowsTemplate='<span class="ag-overlay-loading-center">No data found.</span>'
                />
            </div>
        </main>
    );
});

export default ServerSideTable;
